import Article from "./Article";
import { UnitConverter } from '@vibialighting/vibia-utils';
import BaseBOM from "./BaseBOM";
import ArticleCollection, { getBov } from "./ArticleCollection";
import { convertJSONtoBOM } from "../converters/ProjectConverter";
import Space from "../composition/Space";
import Toggles from "@domain/toggles/Toggles"
import Composition from "../composition/Composition";
import Drivers from "../data/Drivers";
import { assignLightsByAccessoryCapacity } from "../converters/ElectricalGroup"
import { isModelComposingPendants } from "../utils/utils";
import { accessoriesSubfamiliesExcludedInDomain, accessoryProductTypology, PRODUCT_CATEGORIES, PRODUCT_SUBCATEGORIES, PRODUCT_SUBCATEGORIES_OBJECT, SUBFAMILIES_ID } from "../data/Constants"


const repeat = (arr, n) => Array(n).fill(arr).flat();

const toolkitCategory = {
    id: 6,
    description: "TOOLKIT"
}

export default class CreationBOM extends BaseBOM {

    getOrderReferences(articleCollection?: ArticleCollection) {
        const compositions = this.getProps().compositions;
        let articles = []
        for (const composition of compositions) {
            articles = [...articles, ...composition.articles]
        }
        let articleQuantities = {}
        for (const article of articles) {
            const reference = article.referenceCode
            articleQuantities[reference] = articleQuantities[reference] ? articleQuantities[reference] + 1 : 1
        }
        const result = []
        for (const referenceQuantity of Object.entries(articleQuantities)) {
            const [reference, quantity] = referenceQuantity as [string, number]
            const quantityTotal = quantity * this.getProps().quantity
            const leadtime = articleCollection ? this.getReferenceLeadtime(articleCollection, reference, quantityTotal) : null
            const moq = articleCollection ? this.getReferenceMoq(articleCollection, reference) : null
            result.push({
                quantity: quantityTotal,
                reference,
                leadtime,
                moq
            })
        }
        return result
    }

    getModels() {
        const compositions = this.getProps().compositions;
        let articles = []
        for (const composition of compositions) {
            articles = [...articles, ...composition.articles]
            if (composition.accessories !== undefined) {
                articles = [...articles, ...composition.accessories]
            }
        }

        let modelsDict = {}
        for (const article of articles) {
            if (Toggles.isEnabled('ToolkitPlusminusAndSticksAccesoriesPriceFix')) {
                if (
                    accessoriesSubfamiliesExcludedInDomain.includes(article?.subFamily?.id)
                    && article?.articleType?.level1?.id === accessoryProductTypology.id
                ) {
                    continue;
                }
            }

            const reference = article.model
            modelsDict[reference] = true
        }
        return Object.keys(modelsDict)
    }

    getAllocatedCodes(): any[] {
        if (this.props.creationCode) {
            return [this.props.creationCode]
        } else {
            return []
        }
    }

    refreshCodes(articleCollection, allocatedCodes, counters) {

        let counterLW = counters.LW || 0;
        let counterCR = counters.CR || 0;

        const allocated = allocatedCodes.filter(code => code.includes('CR')) // Filter those that contain CR
        const { code, number, counter } = BaseBOM.calculateCode('CR', allocated, counterCR);
        this.props.creationCode = code
        this.props.creationNumber = number

        if (Toggles.isEnabled('creationCodeCounter')) {
            counterCR = counter;
        }

        for (let i = 0; i < this.props.compositions.length; i++) {
            for (let j = 0; j < this.props.compositions[i].articles.length; j++) {
                let articleIterator = convertJSONtoBOM({
                    ...this.props.compositions[i].articles[j],
                    type: 'REGULAR'
                }).getArticles(articleCollection)[0]
                if (articleCollection.articleNeedsLWCode(articleIterator)) {
                    const allocated = allocatedCodes.filter(code => code.includes('LW'))
                    const { code } = BaseBOM.calculateCode(`${articleIterator.getModel()}LW`, allocated, counterLW)
                    this.props.compositions[i].articles[j].articleCode = code;
                    counterLW++
                }
            }
        }

        return { BOM: this, counters: { ...counters, CR: counterCR, LW: counterLW } }
    }

    // TODO: To increase efficiency, we should group exact same articles and change the contract from:
    // Article[] to: {article: Article, quantity: number} []
    getArticles(articleCollection?: ArticleCollection) {
        let articles = []

        for (const composition of this.props.compositions || []) {
            for (const product of composition.articles) {
                articles = [
                    ...articles,
                    ...convertJSONtoBOM({
                        ...product,
                        type: 'REGULAR'
                    }).getArticles(articleCollection)
                ]
            }
        }

        articles = repeat(articles, this.props.quantity)
        return articles
    }

    static groupCompositionArticles(compositionArticles) {
        const groupedCompositionArticles = []

        const hasDefaultHeight = (defaultPendantHeight, pendantHeight) => {
            return pendantHeight === defaultPendantHeight
        }

        compositionArticles.forEach(compositionArticle => {
            const article = new Article(compositionArticle)

            const pendantHeight = article.getAttribute("pendantHeight")

            let defaultPendantHeight = article.getStandardPendantHeight()

            const isPendantHeightEditable = defaultPendantHeight

            const hasPendantHeightAndIsDefault = (pendantHeight || defaultPendantHeight) && hasDefaultHeight(defaultPendantHeight, pendantHeight)
            const articleToUpdate = groupedCompositionArticles.find(groupedArticle => {

                let groupArticle

                if (Toggles.isEnabled('groupBindAndCircusPSA')) {
                    const isSameArticleCode = compositionArticle.articleCode === groupedArticle.articleCode
                    const isSamePendantHeight = groupedArticle.attributes?.pendantHeight?.value === compositionArticle.attributes?.pendantHeight?.value

                    groupArticle = isSameArticleCode && isSamePendantHeight

                } else {
                    const matchingArticleCode = compositionArticle.articleCode === groupedArticle.articleCode
                    groupArticle = matchingArticleCode

                    if (isPendantHeightEditable && matchingArticleCode) {
                        groupArticle = !pendantHeight || hasPendantHeightAndIsDefault
                    }
                }
                return groupArticle
            })

            const updateQuantity = articleToUpdate

            if (updateQuantity) {
                articleToUpdate.quantity++
            } else {
                compositionArticle.quantity = 1
                groupedCompositionArticles.push(compositionArticle)
            }
        })
        return groupedCompositionArticles
    }

    static parseArticlesToComposition(articles: Article[], index: number, composition?: Composition) {
        let totalWatts = 0;
        let totalLumensLed = 0;
        let compositionArticles = [];
        let compositionAccesories = []
        let isComposingPendants = articles.every(article => isModelComposingPendants(article.getModel()))

        let attributes: any = {};

        let attributesKeyExcluded = [];

        let pendantHeight = 0;
        for (const article of articles) {
            if (article.getAttribute('pendantHeight') > pendantHeight) {
                pendantHeight = article.getAttribute('pendantHeight')
            }
            const articleAttributes = article.getProps().attributes;
            for (const attributeKey of Object.keys(articleAttributes)) {
                if (!articleAttributes[attributeKey].id) {
                    continue;
                }

                if (!attributes[attributeKey]) {
                    attributes[attributeKey] = articleAttributes[attributeKey];
                } else if (attributes[attributeKey] && attributes[attributeKey].id !== articleAttributes[attributeKey].id) {
                    attributesKeyExcluded.push(attributeKey);
                }
            }
            totalWatts += article.getProps().watts.value;
            totalLumensLed += article.getProps().lumensLed?.value || null;
            const parsedArticle: any = CreationBOM.parseArticleProperties(article);
            parsedArticle.articleCode = parsedArticle.referenceCode;
            if (Toggles.isEnabled("addAccessoryCodeToAccessories") && article.isAccessory()) {
                parsedArticle.accessoryCode = parsedArticle.articleCode
            }
            parsedArticle.quantity = 1;
            parsedArticle.isAvailablePrice = 1;
            parsedArticle.isPersonalized = +article.isPersonalized();
            if (isComposingPendants && Toggles.isEnabled("AvoidMVariationPrices")) {
                if (composition && composition.allLightsHaveNoDriverOption() && composition.allLightsHaveNoDriver()) {
                    let reference = parsedArticle.referenceCode
                    let controlModifier = composition.getAttributeModifier("control")
                    parsedArticle.priceReference = reference.replace("M", controlModifier)
                }
            }

            if (Toggles.isEnabled('composingPendantsOrdersOnERP')) {
                parsedArticle.type = "REGULAR" // This always asumes all articles are regular (Maybe will change with PSAs are considered)
                parsedArticle.attributes = articleAttributes
            }

            delete parsedArticle.hasAttributes;


            if (article.isAccessory()) {
                compositionAccesories.push(parsedArticle)
            } else {
                compositionArticles.push(parsedArticle)
            }
        }

        compositionArticles = this.groupCompositionArticles(compositionArticles)
        compositionAccesories = this.groupCompositionArticles(compositionAccesories)


        for (const attributeKeyExcluded of attributesKeyExcluded) {
            delete attributes[attributeKeyExcluded];
        }

        if (Toggles.isEnabled('remoteDriverProjectDetailsPage')) {
            if (composition && composition.isRemote()) {
                const newCompositionControl = composition.getAttributeVariation("control")
                if (newCompositionControl) {
                    const parsedNewCompositionControl = {
                        description: newCompositionControl.description,
                        code: newCompositionControl.modifier,
                        bov: getBov(newCompositionControl.type)
                    }
                    attributes.control = parsedNewCompositionControl
                } else {
                    attributes.control = null
                }
            }
        }

        let subFamily = compositionArticles[0]?.subFamily
        let familyId = compositionArticles[0]?.familyId
        subFamily = subFamily || compositionAccesories[0].subFamily
        familyId = familyId || compositionAccesories[0].familyId

        let BOM = {
            compositionNumber: index + 1,
            subFamily: subFamily,
            familyId: familyId,
            attributes,
            totalLumensLed: UnitConverter.toUdObject(
                totalLumensLed,
                UnitConverter.UD_TYPES.LUMENS
            ),
            totalWatts: UnitConverter.toUdObject(
                totalWatts,
                UnitConverter.UD_TYPES.WATTS
            ),
            category: {
                id: Toggles.isEnabled("removeProductCategoryCP") ? 1 : 7,
                description: Toggles.isEnabled("removeProductCategoryCP") ? "REGULAR" : "COMPOSING_PENDANTS",
                subcategory: {} as any
            },
            articles: compositionArticles,
            accessories: compositionAccesories,
            electricalGroups: [],
            packaging: {
                packages: [],
                timing: []
            },
            assemblyData: [{}],
        };
        delete BOM.electricalGroups
        delete BOM.category.subcategory

        if (Toggles.isEnabled("addDriverAccessoryToComposition")) {
            if (composition && composition.isRemote()) {
                const accessories = composition.getDriver()
                const catalog = articles[0].getCatalog() == 21 ? 'usa' : 'row'
                const parsedAccessories = Drivers.parseToAccessories(accessories?.[catalog])
                BOM.accessories = [...BOM.accessories, ...parsedAccessories]
            }
        }

        if (Toggles.isEnabled("removeProductCategoryCP")) {
            if (BOM.accessories && BOM.accessories.length > 0) {
                // TOOLKIT
                BOM.category.id = 6
                BOM.category.description = "TOOLKIT"
            } else {
                // REGULAR
                BOM.category.id = 1
                BOM.category.description = "REGULAR"
            }

            const articlesWithoutAccesories = articles.filter(article => !article.isAccessory())

            const allArticlesFromCP = Toggles.isEnabled('enable2DCompositorForCP') ?
                articlesWithoutAccesories.every(article => isModelComposingPendants(article.getModel())) :
                articles.every(article => isModelComposingPendants(article.getModel()))
            if (allArticlesFromCP) {
                const CPSubcategory = PRODUCT_SUBCATEGORIES_OBJECT.find(PRODUCT_SUBCATEGORY => PRODUCT_SUBCATEGORY.id === PRODUCT_SUBCATEGORIES.COMPOSING_PENDANTS)
                BOM.category.subcategory = { ...CPSubcategory }
            }
        }

        if (Toggles.isEnabled("electricalGroupsInBOM")) {
            const catalog = articles[0].getCatalog() == 21 ? 'usa' : 'row'
            if (composition && composition.isRemote()) {
                BOM.electricalGroups = assignLightsByAccessoryCapacity(composition, catalog)
            }
        }

        if (pendantHeight) {
            BOM.attributes.pendantHeight = UnitConverter.toObjectLengthByCatalog(
                articles[0].getProps().catalog.id,
                Math.ceil(pendantHeight * 100),
                UnitConverter.UD_TYPES.CENTIMETERS
            );
        }

        return BOM;
    }


    static fromArticles(articles: Array<Article>, space?: Space) {
        const articlesByGroup: { [key: string]: Array<Article> } = CreationBOM.groupArticles(articles, space)

        let domainCompositions = []
        if (Toggles.isEnabled("addDriverAccessoryToComposition")) {
            domainCompositions = space.getCompositions()
        }

        const compositions = Object.entries(articlesByGroup).map(([key, value], index) => {
            const composition = domainCompositions.find((comp) => comp.getId() == key)
            return CreationBOM.parseArticlesToComposition(value, index, composition)
        });


        let applicationLocation = compositions[0].articles[0]?.applicationLocation
        applicationLocation = applicationLocation || compositions[0].accessories[0].applicationLocation

        const BOM = {
            applicationLocation: applicationLocation,
            configuratorType: BaseBOM.getConfiguratorType(space),
            compositions,
            type: "CREATION",
            quantity: 1,
            isPersonalized: articles.every(a => a.isPersonalized()) ? 1 : 0,
            validation: {
                status: 0
            },
            familyId: compositions[0].familyId,
            subFamily: {
                ...compositions[0].subFamily,
                prefixCode: "CR"
            },
            category: {
                id: 7,
                description: "COMPOSING_PENDANTS"
            }
        }

        if (Toggles.isEnabled("removeProductCategoryCP")) {
            const areAllCompositionsSameCategory = (compositions: Array<any>): boolean => {
                const firstCompositionCategory = compositions[0].category
                return compositions.every(composition => composition.category.id === firstCompositionCategory.id)
            }
            const sameCategoryCompositions = areAllCompositionsSameCategory(BOM.compositions)
            let creationCategory = { ...compositions[0].category }

            if (!sameCategoryCompositions) {
                const prioritizedCategory = PRODUCT_CATEGORIES.TOOLKIT
                const prioritizedComposition = BOM.compositions.find(composition => composition.category.id === prioritizedCategory)
                const priorityCategory = prioritizedComposition?.category
                if (priorityCategory) {
                    creationCategory = { ...priorityCategory }
                }
            }

            BOM.category = creationCategory
        }

        return new CreationBOM(BOM)
    }

    static groupArticles(articles: Article[], space: Space): { [key: string]: Article[]; } {
        return articles.reduce((acc, item) => {
            let id = item.getProps().grandparentId
            const isComplement = item.isAccessory()

            if (isComplement) {
                id = item.getProps().parentId
            }

            if (id === space.getId()) {
                return {
                    ...acc,
                    [item.getProps().id]: [item]
                };
            }

            if (acc[id]?.length) {
                return {
                    ...acc,
                    [id]: [
                        ...acc[id],
                        item
                    ]
                };
            }

            return {
                ...acc,
                [id]: [item]
            }

        }, {})
    }
}